/**
 * useContentScans — lists the current member's recent content_scans
 * rows and exposes a per-asset rescan action.  Used by the KYC panel
 * to surface which photos / voice / video have been screened and let
 * the member retry a flagged one.
 */
import { useCallback, useEffect, useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/lib/auth/AuthProvider";

export type ContentScanTarget =
  | "avatar"
  | "photo"
  | "voice_intro"
  | "video_intro"
  | "message_attachment";

export type ContentScanStatus =
  | "queued"
  | "in_progress"
  | "passed"
  | "flagged"
  | "failed"
  | "manual_review";

export interface ContentScanRow {
  id: string;
  user_id: string;
  target_kind: ContentScanTarget;
  target_ref: string;
  provider: string;
  status: ContentScanStatus;
  category_scores: Record<string, number>;
  flags: string[];
  reviewer_id: string | null;
  reviewed_at: string | null;
  requested_at: string;
  completed_at: string | null;
  attempts: number;
  created_at: string;
  updated_at: string;
}

interface RescanResult {
  row?: ContentScanRow;
  error?: string;
}

const RECENT_LIMIT = 25;

export const useContentScans = () => {
  const { user } = useAuth();
  const [rows, setRows] = useState<ContentScanRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [busyTarget, setBusyTarget] = useState<string | null>(null);

  const refresh = useCallback(async () => {
    if (!user) {
      setRows([]);
      setLoading(false);
      return;
    }
    const { data } = await (supabase as any)
      .from("content_scans")
      .select("*")
      .eq("user_id", user.id)
      .order("created_at", { ascending: false })
      .limit(RECENT_LIMIT);
    setRows((data ?? []) as ContentScanRow[]);
    setLoading(false);
  }, [user]);

  useEffect(() => {
    void refresh();
  }, [refresh]);

  useEffect(() => {
    if (!user) return;
    const channel = supabase
      .channel(`content_scans:${user.id}`)
      .on(
        "postgres_changes",
        {
          event: "*",
          schema: "public",
          table: "content_scans",
          filter: `user_id=eq.${user.id}`,
        },
        () => {
          void refresh();
        },
      )
      .subscribe();
    return () => {
      void supabase.removeChannel(channel);
    };
  }, [user, refresh]);

  const rescan = useCallback(
    async (
      target_kind: ContentScanTarget,
      target_ref: string,
    ): Promise<RescanResult> => {
      const key = `${target_kind}:${target_ref}`;
      setBusyTarget(key);
      const { data, error } = await supabase.functions.invoke(
        "request-bgc-scan",
        { body: { kind: "content_rescan", target_kind, target_ref } },
      );
      setBusyTarget(null);
      if (error) return { error: error.message };
      const payload = data as { row?: ContentScanRow; error?: string } | null;
      if (payload?.row) return { row: payload.row };
      return { error: payload?.error ?? "unknown_error" };
    },
    [],
  );

  // Most recent scan per (target_kind, target_ref).  Useful for the
  // "current screening status" tile in the UI.
  const latestByTarget = (() => {
    const m = new Map<string, ContentScanRow>();
    for (const r of rows) {
      const k = `${r.target_kind}:${r.target_ref}`;
      if (!m.has(k)) m.set(k, r);
    }
    return m;
  })();

  return {
    rows,
    latestByTarget,
    loading,
    busyTarget,
    refresh,
    rescan,
  };
};
